import { Col, Flex, Row, Typography } from "antd";
import LimitState from "./LimitState";
import { LimitDetailProps } from "./LimitDetails";
import AlertTypeId from "src/consts/AlertTypeId";
import AlertDTO from "src/models/AlertDTO";
import React from "react";

interface LimitsStateViewProps{
    limitStates: AlertDTO[];
    onClickLimit: (limit: LimitDetailProps | null | undefined) => void;
}

const { Text } = Typography;

const LimitsStateView : React.FC<LimitsStateViewProps> = ({ limitStates, onClickLimit }) => {
    
    const machineOverTemp = limitStates.find( l => l.alertTypeId === AlertTypeId.MachineGearBox);
    const machineOverCurrent1 = limitStates.find( l => l.alertTypeId === AlertTypeId.MachineOverCurrent1);
    const machineOverCurrent2 = limitStates.find( l => l.alertTypeId === AlertTypeId.MachineOverCurrent2);
    const augerOverCurrent = limitStates.filter(l => l.alertTypeId === AlertTypeId.AugerOverCurrent && l.lastTriggerTime != null);
    const fanOverCurrent = limitStates.filter(l => l.alertTypeId === AlertTypeId.FanOverCurrent && l.lastTriggerTime != null);
    const remoteStop = limitStates.find(l => l.alertTypeId === AlertTypeId.RemoteStop);    
    const plenumOverTemp = limitStates.find( l => l.alertTypeId === AlertTypeId.PlenumOverTemp);
    const grainOverTemp = limitStates.find( l => l.alertTypeId === AlertTypeId.GrainOverTemp); 

    return(
        <Flex justify='center' vertical style={{ maxWidth:'800px', paddingTop: '8px', paddingBottom: '8px' }}>
            <Row>
                <Text style={{ fontSize:'12px', fontWeight:'bold' }}>When a limit switch is tripped, the associated equipment will stop and must be reset.</Text>
            </Row>
            <br/>
            <Row wrap justify='center' gutter={[16, 16]}>      
                <Col 
                onClick={() => onClickLimit({
                    title: 'Machine motor over temp',
                    description: machineOverTemp?.message ?? 'No description available.',
                    perps: [machineOverTemp?.triggerReason ?? 'Unknown'],
                    tripped: machineOverTemp?.lastTriggerTime != null
                })}>
                    <LimitState 
                    title={'Machine motor over temp'}
                    subTitle={
                        machineOverTemp?.lastTriggerTime != null ? 
                        `${machineOverTemp?.alertTypeName}: Press here for details`
                        : 
                        'When tripped, fans shut down & won\'t restart'
                    }
                    tripped={machineOverTemp?.lastTriggerTime != null}
                    />
                </Col>
                <Col 
                onClick={() => onClickLimit({
                    title: 'Machine 1 Motor Over Current',
                    description: machineOverCurrent1?.message ?? 'No description available.',
                    perps: [machineOverCurrent1?.triggerReason ?? 'Unknown'],
                    tripped: machineOverCurrent1?.lastTriggerTime != null 
                })}>
                    <LimitState 
                    title={'Machine 1 Motor Over Current'}
                    subTitle={
                        machineOverCurrent1?.lastTriggerTime != null ? 
                        `${machineOverCurrent1?.alertTypeName}: Press here for details`
                        : 
                        'When tripped, machines shut down & won\'t restart'
                    }
                    tripped={machineOverCurrent1?.lastTriggerTime != null}
                    />
                </Col>
                <Col 
                onClick={() => onClickLimit({
                    title: 'Machine 2 Motor Over Current',
                    description: machineOverCurrent2?.message ?? 'No description available.',
                    perps: [machineOverCurrent2?.triggerReason ?? 'Unknown'],
                    tripped: machineOverCurrent2?.lastTriggerTime != null 
                })}>
                    <LimitState 
                    title={'Machine 2 Motor Over Current'}
                    subTitle={
                        machineOverCurrent2?.lastTriggerTime != null ? 
                        `${machineOverCurrent2?.alertTypeName}: Press here for details`
                        : 
                        'When tripped, machines shut down & won\'t restart'
                    }
                    tripped={machineOverCurrent2?.lastTriggerTime != null}
                    />
                </Col>
                <Col 
                onClick={() => onClickLimit({
                    title: 'Auger over current',
                    description: 'When tripped, auger shutdowns & won\'t restart',
                    perps: augerOverCurrent.map(a => a.triggerReason ?? 'Unknown'),
                    tripped: augerOverCurrent.length > 0
                })}>
                    <LimitState 
                    title={'Auger over current'}
                    subTitle={
                        augerOverCurrent?.length > 0 ? 
                        `${augerOverCurrent[0].alertTypeName}: Press here for details`
                        : 
                        'When tripped, machines shut down & won\'t restart'
                    }
                    tripped={augerOverCurrent.length > 0}
                    />
                </Col>
                <Col
                onClick={() => onClickLimit({
                    title: 'Fan over current',
                    description: 'When tripped, fans shutdown & won\'t restart',
                    perps: fanOverCurrent.map(f => f.triggerReason ?? 'Unknown'),
                    tripped: fanOverCurrent.length > 0
                })}
                >
                    <LimitState 
                    title={'Fan over current'}
                    subTitle={
                        fanOverCurrent?.length > 0 ? 
                        `${fanOverCurrent[0].alertTypeName}: Press here for details`
                        : 
                        'When tripped, fans shut down & won\'t restart'
                    }
                    tripped={fanOverCurrent.length > 0}
                    />
                </Col>
                <Col onClick={() => onClickLimit({
                    title: 'Remote Stop',
                    description: remoteStop?.message ?? 'No description available.',
                    perps: [remoteStop?.triggerReason ?? 'Unknown'],
                    tripped: remoteStop?.lastTriggerTime != null 
                })
                }>
                    <LimitState 
                    title={'Remote Stop'}
                    subTitle={
                        remoteStop?.lastTriggerTime != null  ? 
                        `${remoteStop?.alertTypeName}: Press here for details`
                        : 
                        'When tripped, machines shut down & won\'t restart'
                    }
                    tripped={remoteStop?.lastTriggerTime != null }
                    />
                </Col>
                <Col onClick={() => onClickLimit({
                    title: 'Plenum Over Temp',
                    description: plenumOverTemp?.message ?? 'No description available.',
                    perps: [plenumOverTemp?.triggerReason ?? 'Unknown'],
                    tripped: plenumOverTemp?.lastTriggerTime != null 
                })}>
                    <LimitState 
                    title={'Plenum Over Temp'}
                    subTitle={
                        plenumOverTemp?.lastTriggerTime != null ? 
                        `${plenumOverTemp?.alertTypeName}: Press here for details`
                        : 
                        'When tripped, heaters shut down & won\'t restart'
                    }
                    tripped={plenumOverTemp?.lastTriggerTime != null }
                    />
                </Col>
                <Col onClick={() => onClickLimit({
                    title: 'Grain Over Temp',
                    description: grainOverTemp?.message ?? 'No description available.',
                    perps: [grainOverTemp?.triggerReason ?? 'Unknown'],
                    tripped: grainOverTemp?.lastTriggerTime != null 
                })}>
                    <LimitState 
                    title={'Grain Over Temp'}
                    subTitle={
                        grainOverTemp?.lastTriggerTime != null  ? 
                        `${grainOverTemp?.alertTypeName}: Press here for details`
                        : 
                        'When tripped, fans shut down & won\'t restart'
                    }
                    tripped={grainOverTemp?.lastTriggerTime != null }
                    />
                </Col>
            </Row>
            <br/>
        </Flex>
    )
}

export default LimitsStateView;